import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const TechnicalSEO = ({ 
  pageSpeed = true,
  structuredData = true,
  securityHeaders = true,
  performanceOptimization = true
}) => {
  useEffect(() => {
    // Core Web Vitals optimization
    if (pageSpeed) {
      optimizePageSpeed();
    }
    
    // Security headers implementation
    if (securityHeaders) {
      implementSecurityHeaders();
    }
    
    // Performance monitoring
    if (performanceOptimization) {
      monitorPerformance();
    }
  }, [pageSpeed, securityHeaders, performanceOptimization]);

  const optimizePageSpeed = () => {
    // Lazy loading for images
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.classList.remove('lazy');
              imageObserver.unobserve(img);
            }
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }

    // Note: Removed preload for missing resources (logo.png, maxicar-hero.jpg, inter-var.woff2)
    // Add these files to public/images and public/fonts directories when available
  };

  const implementSecurityHeaders = () => {
    // Content Security Policy - Comprehensive configuration for all services
    const cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    cspMeta.content = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com https://googleads.g.doubleclick.net https://www.googleadservices.com https://unpkg.com",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://unpkg.com",
      "font-src 'self' https://fonts.gstatic.com data:",
      "img-src 'self' data: https: blob:",
      "connect-src 'self' https://taxibooking-backend.fly.dev https://www.google-analytics.com https://www.googletagmanager.com https://googleads.g.doubleclick.net https://www.googleadservices.com https://www.google.com https://google.com https://ad.doubleclick.net https://*.tile.openstreetmap.org https://nominatim.openstreetmap.org",
      "frame-src 'self' https://www.openstreetmap.org https://www.google.com",
      "object-src 'none'",
      "base-uri 'self'",
      "form-action 'self'"
    ].join('; ');
    document.head.appendChild(cspMeta);

    // Additional security headers (removed X-Frame-Options as it can't be set via meta tag)
    const securityHeaders = [
      { httpEquiv: 'X-Content-Type-Options', content: 'nosniff' },
      { httpEquiv: 'X-XSS-Protection', content: '1; mode=block' },
      { httpEquiv: 'Referrer-Policy', content: 'strict-origin-when-cross-origin' },
      { httpEquiv: 'Permissions-Policy', content: 'geolocation=(), microphone=(), camera=()' }
    ];

    securityHeaders.forEach(header => {
      const meta = document.createElement('meta');
      meta.httpEquiv = header.httpEquiv;
      meta.content = header.content;
      document.head.appendChild(meta);
    });
  };

  const monitorPerformance = () => {
    // Core Web Vitals monitoring
    if ('PerformanceObserver' in window) {
      // Largest Contentful Paint (LCP)
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        console.log('LCP:', lastEntry.renderTime || lastEntry.loadTime);
        
        // Send to analytics if needed
        if (window.gtag) {
          window.gtag('event', 'LCP', {
            event_category: 'Web Vitals',
            value: Math.round(lastEntry.renderTime || lastEntry.loadTime)
          });
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          console.log('FID:', entry.processingStart - entry.startTime);
          
          if (window.gtag) {
            window.gtag('event', 'FID', {
              event_category: 'Web Vitals',
              value: Math.round(entry.processingStart - entry.startTime)
            });
          }
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach(entry => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        console.log('CLS:', clsValue);
        
        if (window.gtag) {
          window.gtag('event', 'CLS', {
            event_category: 'Web Vitals',
            value: Math.round(clsValue * 1000)
          });
        }
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    }

    // Monitor page load time
    window.addEventListener('load', () => {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      console.log('Page load time:', loadTime + 'ms');
      
      if (window.gtag) {
        window.gtag('event', 'page_load_time', {
          event_category: 'Performance',
          value: loadTime
        });
      }
    });
  };

  const generatePerformanceOptimization = () => {
    return (
      <script>
        {`
          // Service Worker Registration
          if ('serviceWorker' in navigator) {
            window.addEventListener('load', () => {
              navigator.serviceWorker.register('/sw.js')
                .then(registration => {
                  console.log('SW registered: ', registration);
                })
                .catch(registrationError => {
                  console.log('SW registration failed: ', registrationError);
                });
            });
          }

          // Resource Hints - DNS prefetch
          (function() {
            var dnsDomains = [
              '//www.google-analytics.com',
              '//www.googletagmanager.com',
              '//fonts.googleapis.com',
              '//fonts.gstatic.com',
              '//www.google.com',
              '//www.googleadservices.com',
              '//ad.doubleclick.net'
            ];
            dnsDomains.forEach(function(domain) {
              var link = document.createElement('link');
              link.rel = 'dns-prefetch';
              link.href = domain;
              document.head.appendChild(link);
            });
          })();

          // Resource Hints - Preconnect
          (function() {
            var preconnectDomains = [
              { domain: 'https://taxibooking-backend.fly.dev', crossOrigin: false },
              { domain: 'https://www.google-analytics.com', crossOrigin: false },
              { domain: 'https://www.googletagmanager.com', crossOrigin: false },
              { domain: 'https://fonts.googleapis.com', crossOrigin: false },
              { domain: 'https://fonts.gstatic.com', crossOrigin: true }
            ];
            preconnectDomains.forEach(function(item) {
              var link = document.createElement('link');
              link.rel = 'preconnect';
              link.href = item.domain;
              if (item.crossOrigin) link.crossOrigin = 'anonymous';
              document.head.appendChild(link);
            });
          })();
        `}
      </script>
    );
  };

  return (
    <Helmet>
      {/* Technical SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, shrink-to-fit=no" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Performance Optimization */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      
      {/* Note: Removed preload for missing resources - add files when available */}
      
      {/* Cache Control */}
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000, immutable" />
      
      {/* Compression */}
      <meta httpEquiv="Accept-Encoding" content="gzip, deflate, br" />
      
      {/* Performance Script */}
      {generatePerformanceOptimization()}
      
      {/* Schema.org Technical Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "MaxiCar Worms",
          "url": "https://www.maxicar-worms.de",
          "description": "#1 Bester Taxi Service in Worms - Online Buchung in 30 Sekunden",
          "publisher": {
            "@type": "Organization",
            "name": "MaxiCar Worms",
            "url": "https://www.maxicar-worms.de"
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.maxicar-worms.de/booking?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      
      {/* Technical Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": "https://www.maxicar-worms.de#organization",
          "name": "MaxiCar Worms",
          "url": "https://www.maxicar-worms.de",
          "logo": "https://www.maxicar-worms.de/images/logo.png",
          "description": "#1 Bester Taxi Service in Worms mit 24/7 Verfügbarkeit",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Bahnhofstraße 30",
            "addressLocality": "Worms",
            "addressRegion": "Rheinland-Pfalz",
            "postalCode": "67547",
            "addressCountry": "DE"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+49 6241 4700",
            "contactType": "customer service",
            "availableLanguage": ["German", "English"]
          },
          "sameAs": [
            "https://www.facebook.com/MaxiCarWorms",
            "https://twitter.com/MaxiCarWorms",
            "https://www.instagram.com/maxicarworms"
          ]
        })}
      </script>
      
      {/* Website Performance Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "MaxiCar Taxi Booking App",
          "url": "https://www.maxicar-worms.de",
          "applicationCategory": "TravelApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR"
          },
          "featureList": [
            "Online Taxi Booking",
            "Real-time Driver Tracking",
            "Secure Payment Processing",
            "24/7 Customer Support",
            "Multi-language Support"
          ]
        })}
      </script>
    </Helmet>
  );
};

export default TechnicalSEO;
